import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const boundedDescription = z.string().trim().min(1).max(2_000)
const capturedAt = z.iso.datetime({ offset: true })

export const fieldBeforeAfterRoleV1Schema = z.enum(["Before", "After"])
export const fieldBeforeAfterSourceV1Schema = z.enum([
  "Camera",
  "Photo Library",
])

export const fieldBeforeAfterMediaV1Schema = z
  .object({
    mediaId: entityIdSchema,
    mediaType: z.enum(["image/jpeg", "image/png"]),
    source: fieldBeforeAfterSourceV1Schema,
    capturedAt,
  })
  .strict()

export const fieldBeforeAfterSetV1Schema = z
  .object({
    setId: entityIdSchema,
    recordVersion: z.number().int().positive().safe(),
    beforeDescription: boundedDescription.nullable(),
    afterDescription: boundedDescription.nullable(),
    isClientVisible: z.boolean(),
    beforeMedia: z.array(fieldBeforeAfterMediaV1Schema).max(25),
    afterMedia: z.array(fieldBeforeAfterMediaV1Schema).max(25),
  })
  .strict()
  .superRefine((value, context) => {
    const mediaIds = [...value.beforeMedia, ...value.afterMedia].map(
      (media) => media.mediaId,
    )
    if (new Set(mediaIds).size !== mediaIds.length) {
      context.addIssue({
        code: "custom",
        message: "Before/After media must be unique within a set.",
        path: ["beforeMedia"],
      })
    }
  })

const visitMutationBase = {
  setId: entityIdSchema,
  visitId: entityIdSchema,
  capturedAt,
}

export const fieldBeforeAfterCreateV1Schema = z
  .object(visitMutationBase)
  .strict()

export const fieldBeforeAfterDescriptionUpdateV1Schema = z
  .object({
    ...visitMutationBase,
    role: fieldBeforeAfterRoleV1Schema,
    description: boundedDescription,
  })
  .strict()

export const fieldBeforeAfterVisibilityUpdateV1Schema = z
  .object({
    ...visitMutationBase,
    isClientVisible: z.boolean(),
  })
  .strict()

export const fieldBeforeAfterRemoveV1Schema = z
  .object(visitMutationBase)
  .strict()

export const fieldBeforeAfterMediaRemoveV1Schema = z
  .object({
    ...visitMutationBase,
    mediaId: entityIdSchema,
  })
  .strict()

export type FieldBeforeAfterRoleV1 = z.infer<
  typeof fieldBeforeAfterRoleV1Schema
>
export type FieldBeforeAfterSourceV1 = z.infer<
  typeof fieldBeforeAfterSourceV1Schema
>
export type FieldBeforeAfterMediaV1 = z.infer<
  typeof fieldBeforeAfterMediaV1Schema
>
export type FieldBeforeAfterSetV1 = z.infer<
  typeof fieldBeforeAfterSetV1Schema
>
